/**
 * Event Collector 클라이언트 SDK (순수 JavaScript)
 * 
 * 특징:
 * - 초경량 (< 3KB minified)
 * - 의존성 없음
 * - 실패 시 재시도 (Exponential Backoff)
 * - 배치 전송 (성능 최적화)
 * - localStorage 큐 (오프라인 지원)
 */

(function (global) {
  'use strict';

  function generateSecureId(prefix) {
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      var array = new Uint8Array(12);
      crypto.getRandomValues(array);
      var hex = Array.from(array).map(function(b) {
        return b.toString(16).padStart(2, '0');
      }).join('');
      return prefix + Date.now().toString(36) + '_' + hex;
    }
    return prefix + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
         + Math.random().toString(36).substr(2, 9);
  }

  function fnv1aHash(str) {
    var hash = 0x811c9dc5;
    for (var i = 0; i < str.length; i++) {
      hash ^= str.charCodeAt(i);
      hash = (hash * 0x01000193) >>> 0;
    }
    return hash.toString(16);
  }

  // visitor_id 크로스서브도메인 공유: 폴백 visitor_id를 localStorage(호스트 격리) 대신
  // '부모 도메인 쿠키'에 저장해 서브도메인 간(예: star-workout.com ↔ qualification.star-workout.com)
  // 같은 사람으로 유지한다. localStorage는 절대 크로스서브도메인 공유 불가.
  function deriveParentDomain(hostname) {
    if (!hostname || hostname === 'localhost' || /^[0-9.]+$/.test(hostname)) return null;
    var parts = hostname.split('.');
    if (parts.length < 2) return null;
    var lastTwo = parts.slice(-2).join('.');
    // co.kr 등 2단 ccTLD는 라벨 3개로(.star-workout.co.kr). 그 외는 .star-workout.com/.da-si.com/.star-law.com
    if (['co.kr', 'com.au'].indexOf(lastTwo) !== -1) return '.' + parts.slice(-3).join('.');
    return '.' + lastTwo;
  }

  // config.cookieDomain(명시) 우선 → crossSubdomain(기본 ON) 시 부모 도메인 자동 도출 → 아니면 null(현재 호스트).
  function resolveVisitorCookieDomain(config) {
    if (config && config.cookieDomain) return config.cookieDomain;        // 도메인 직접 입력
    if (config && config.crossSubdomain === false) return null;            // 명시적 opt-out
    if (typeof window === 'undefined' || !window.location) return null;
    return deriveParentDomain(window.location.hostname);                   // 기본: 서브도메인 허용(자동)
  }

  /**
   * Analytics 클래스
   */
  function Analytics(config) {
    this.endpoint = config.endpoint || 'http://localhost:5231';
    this.apiKey = config.apiKey || '';
    this.batchSize = config.batchSize || 10;
    this.flushInterval = config.flushInterval || 5000;
    this.maxRetries = config.maxRetries || 3;
    this.retryDelay = config.retryDelay || 1000;
    // Custom headers added on every request. Used for ALB header-based routing
    // (ex: { 'X-Verify-Target': 'shadow-green' } to force routing to verify TG).
    this.extraHeaders = (config.extraHeaders && typeof config.extraHeaders === 'object')
      ? config.extraHeaders
      : {};
    
    this.queue = [];
    this.flushTimer = null;
    this.fpjsVisitorId = null;
    this.cookieDomain = resolveVisitorCookieDomain(config);
    this.visitorId = this._getOrCreateVisitorId();
    this.sessionId = this._getOrCreateSessionId();
    this.selfFingerprint = this._collectFingerprint();
    this.landingContext = this._getOrCreateLandingContext();
    this.clientPageInstanceId = null;
    this.clientPageInstancePath = null;
    this.pageContext = this._readPageInstanceContext();
    this.pageAlbTrace = this.pageContext.pageAlbTraceId;
    // page_view 시점에 확정한 페이지 컨텍스트(S2). 폼제출 스냅샷이 이걸 재사용해 page_view와
    // 동일한 page_instance_id를 싣도록 한다(요청마다 pi_ 재발급/프리페치 오염과 무관하게 앵커 일치).
    this.activePageContext = null;

    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this._handleBeforeUnload.bind(this));
    }
    
    this._startBatchFlusher();
  }

  Analytics.prototype._getOrCreateVisitorId = function () {
    if (typeof window === 'undefined') return null;

    var storageKey = 'analytics_visitor_id';
    // 1) 부모 도메인 쿠키 우선(서브도메인 간 공유)
    var visitorId = this._readCookie(storageKey);
    // 2) 레거시 localStorage 값 마이그레이션(기존 사용자 id 보존)
    if (!visitorId) {
      try { visitorId = localStorage.getItem(storageKey); } catch (e) { /* ignore */ }
    }
    // 3) 없으면 신규 발급
    if (!visitorId) {
      visitorId = generateSecureId('vid_');
    }
    // 부모 도메인 쿠키로 (재)기록 → 크로스서브도메인 공유. localStorage도 유지(읽기 호환).
    this._writeVisitorCookie(storageKey, visitorId);
    try { localStorage.setItem(storageKey, visitorId); } catch (e) { /* ignore */ }

    return visitorId;
  };

  Analytics.prototype._writeVisitorCookie = function (name, value) {
    if (typeof document === 'undefined') return;
    try {
      var c = name + '=' + encodeURIComponent(value)
        + '; path=/; max-age=' + (60 * 60 * 24 * 365) + '; SameSite=Lax';
      if (this.cookieDomain) c += '; domain=' + this.cookieDomain;
      document.cookie = c;
    } catch (e) { /* ignore */ }
  };

  Analytics.prototype._getOrCreateSessionId = function () {
    if (typeof window === 'undefined') return null;

    var storageKey = 'analytics_session_id';
    var sessionId = sessionStorage.getItem(storageKey);

    if (!sessionId) {
      sessionId = generateSecureId('sess_');
      sessionStorage.setItem(storageKey, sessionId);
    }

    return sessionId;
  };

  Analytics.prototype._collectFingerprint = function() {
    if (typeof window === 'undefined') return null;
    try {
      var signals = [
        navigator.userAgent || '',
        navigator.language || '',
        navigator.platform || '',
        String(navigator.hardwareConcurrency || 0),
        String(navigator.deviceMemory || 0),
        String(screen.width) + 'x' + String(screen.height),
        String(screen.colorDepth || 0),
        String(window.devicePixelRatio || 1),
        String(new Date().getTimezoneOffset()),
        String(navigator.maxTouchPoints || 0),
      ];

      try {
        var canvas = document.createElement('canvas');
        canvas.width = 200; canvas.height = 50;
        var ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.textBaseline = 'top';
          ctx.font = '14px Arial';
          ctx.fillStyle = '#f60';
          ctx.fillRect(0, 0, 200, 50);
          ctx.fillStyle = '#069';
          ctx.fillText('EC Fingerprint', 2, 2);
          signals.push(canvas.toDataURL());
        }
      } catch(e) { /* canvas not supported */ }

      return 'fp_' + fnv1aHash(signals.join('|'));
    } catch(e) {
      return null;
    }
  };

  Analytics.prototype._getOrCreateLandingContext = function () {
    if (typeof window === 'undefined') {
      return { landingUrl: null, landingQuery: null };
    }

    var urlKey = 'analytics_landing_url';
    var queryKey = 'analytics_landing_query';
    var landingUrl = sessionStorage.getItem(urlKey);
    var landingQuery = sessionStorage.getItem(queryKey);

    if (!landingUrl) {
      landingUrl = window.location.href;
      sessionStorage.setItem(urlKey, landingUrl);
    }

    if (!landingQuery) {
      landingQuery = window.location.search || '';
      sessionStorage.setItem(queryKey, landingQuery);
    }

    return {
      landingUrl: landingUrl,
      landingQuery: landingQuery
    };
  };

  Analytics.prototype._parseQueryParams = function (rawQuery) {
    var query = rawQuery || '';
    if (!query) return {};

    var normalizedQuery = query.charAt(0) === '?' ? query.slice(1) : query;
    var params = new URLSearchParams(normalizedQuery);

    return {
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      utm_content: params.get('utm_content') || undefined,
      utm_term: params.get('utm_term') || undefined,
      gclid: params.get('gclid') || undefined,
      fbclid: params.get('fbclid') || undefined,
      n_media: params.get('n_media') || undefined,
      n_query: params.get('n_query') || undefined,
      gad_source: params.get('gad_source') || undefined,
      gad_campaignid: params.get('gad_campaignid') || undefined
    };
  };

  Analytics.prototype._readMeta = function (name) {
    if (typeof document === 'undefined') return null;
    try {
      var el = document.querySelector('meta[name="' + name + '"]');
      var value = el && el.getAttribute ? el.getAttribute('content') : null;
      if (typeof value !== 'string') return null;
      value = value.trim();
      return value.length > 0 ? value : null;
    } catch (e) {
      return null;
    }
  };

  Analytics.prototype._readCookie = function (name) {
    if (typeof document === 'undefined') return null;
    try {
      var prefix = name + '=';
      var parts = String(document.cookie || '').split(';');
      for (var i = 0; i < parts.length; i++) {
        var part = parts[i].trim();
        if (part.indexOf(prefix) === 0) {
          return decodeURIComponent(part.slice(prefix.length));
        }
      }
    } catch (e) { /* ignore */ }
    return null;
  };

  Analytics.prototype._readPageContextCookie = function () {
    var raw = this._readCookie('ec_page_ctx');
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      var now = Date.now();
      var currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
      if (!parsed || typeof parsed !== 'object') return null;
      if (typeof parsed.id !== 'string' || parsed.id.indexOf('pg_') !== 0) return null;
      if (typeof parsed.path !== 'string' || parsed.path !== currentPath) return null;
      if (typeof parsed.ts !== 'number' || now - parsed.ts > 2 * 60 * 1000) return null;
      return parsed;
    } catch (e) {
      return null;
    }
  };

  Analytics.prototype._readPageAlbTrace = function () {
    return this._readMeta('ec:page-alb-trace');
  };

  Analytics.prototype._getClientPageInstanceId = function () {
    var currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    if (!this.clientPageInstanceId || this.clientPageInstancePath !== currentPath) {
      this.clientPageInstanceId = generateSecureId('pgc_');
      this.clientPageInstancePath = currentPath;
    }
    return this.clientPageInstanceId;
  };

  Analytics.prototype._readPageInstanceContext = function () {
    var pageAlbTraceMeta = this._readMeta('ec:page-alb-trace');
    var pageInstanceMeta = this._readMeta('ec:page-instance-id');
    var serverVisitMeta = this._readMeta('ec:server-visit-id');
    var cookieCtx = pageInstanceMeta ? null : this._readPageContextCookie();
    var serverVisitCookie = this._readCookie('ec_server_visit_id');

    var pageInstanceId = pageInstanceMeta || (cookieCtx && cookieCtx.id) || this._getClientPageInstanceId();
    var pageInstanceIdSource = pageInstanceMeta
      ? 'meta'
      : (cookieCtx && cookieCtx.id ? 'cookie_fallback' : 'client_fallback');
    var pageAlbTraceId = pageAlbTraceMeta || (cookieCtx && typeof cookieCtx.trace === 'string' ? cookieCtx.trace : null);
    var pageAlbTraceIdSource = pageAlbTraceMeta
      ? 'meta'
      : (pageAlbTraceId ? 'cookie_fallback' : 'missing');

    return {
      pageInstanceId: pageInstanceId,
      pageInstanceIdSource: pageInstanceIdSource,
      pageAlbTraceId: pageAlbTraceId,
      pageAlbTraceIdSource: pageAlbTraceIdSource,
      serverVisitId: serverVisitMeta || serverVisitCookie || null,
    };
  };

  Analytics.prototype.refreshPageContext = function () {
    this.pageContext = this._readPageInstanceContext();
    this.pageAlbTrace = this.pageContext.pageAlbTraceId;
  };

  /**
   * 이벤트 전송 (재시도 로직 포함)
   */
  Analytics.prototype._sendWithRetry = function (url, data, retries) {
    var self = this;
    retries = retries || 0;
    
    var headers = Object.assign(
      {
        'Content-Type': 'application/json',
        'X-API-Key': self.apiKey
      },
      self.extraHeaders || {}
    );

    return fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(data),
      keepalive: true // beforeunload에서도 전송 보장
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('HTTP ' + response.status);
        }
        return response.json();
      })
      .catch(function (error) {
        // 재시도
        if (retries < self.maxRetries) {
          var delay = self.retryDelay * Math.pow(2, retries); // Exponential backoff
          console.warn('[Analytics] 재시도 ' + (retries + 1) + '/' + self.maxRetries + ' (' + delay + 'ms)');
          
          return new Promise(function (resolve) {
            setTimeout(function () {
              resolve(self._sendWithRetry(url, data, retries + 1));
            }, delay);
          });
        }
        
        // 최대 재시도 초과 → localStorage에 저장 (오프라인 큐)
        console.error('[Analytics] 전송 실패, localStorage에 저장:', error);
        self._saveToOfflineQueue(data);
        throw error;
      });
  };

  /**
   * 오프라인 큐에 저장
   */
  Analytics.prototype._saveToOfflineQueue = function (data) {
    if (typeof localStorage === 'undefined') return;
    
    try {
      var queue = JSON.parse(localStorage.getItem('analytics_offline_queue') || '[]');
      queue.push({ data: data, timestamp: Date.now() });
      
      // 최대 100개만 저장
      if (queue.length > 100) {
        queue = queue.slice(-100);
      }
      
      localStorage.setItem('analytics_offline_queue', JSON.stringify(queue));
    } catch (e) {
      console.error('[Analytics] 오프라인 큐 저장 실패:', e);
    }
  };

  /**
   * 오프라인 큐 재전송 시도
   */
  Analytics.prototype._retryOfflineQueue = function () {
    if (typeof localStorage === 'undefined') return;
    
    try {
      var queue = JSON.parse(localStorage.getItem('analytics_offline_queue') || '[]');
      if (queue.length === 0) return;
      
      console.log('[Analytics] 오프라인 큐 재전송 시도:', queue.length + '개');
      
      var self = this;
      queue.forEach(function (item) {
        if (Array.isArray(item.data)) {
          self._sendWithRetry(self.endpoint + '/events/batch', item.data, 0);
        } else {
          self._sendWithRetry(self.endpoint + '/events', item.data, 0);
        }
      });
      
      // 성공 시 큐 비우기
      localStorage.removeItem('analytics_offline_queue');
    } catch (e) {
      console.error('[Analytics] 오프라인 큐 재전송 실패:', e);
    }
  };

  /**
   * 이벤트 트래킹
   */
  Analytics.prototype.track = function (eventName, properties) {
    properties = properties || {};
    // SPA/동적 head 갱신 케이스를 위해 track 직전에 meta를 재읽는다.
    this.refreshPageContext();
    var landingQuery = this.landingContext ? this.landingContext.landingQuery : '';
    var marketingContext = this._parseQueryParams(landingQuery);
    var pageContext = this.pageContext || this._readPageInstanceContext();

    // form_submit: 앱이 extra_properties.ad_vendor_load 를 안 실으면 SDK가 스냅샷을 붙인다.
    if (eventName === 'form_submit') {
      try {
        var extra = properties.extra_properties;
        if (!extra || typeof extra !== 'object') {
          extra = {};
        } else {
          // 호출자 object를 mutate하지 않도록 얕은 복사
          extra = Object.assign({}, extra);
        }
        if (!extra.ad_vendor_load) {
          extra.ad_vendor_load = this.getAdVendorLoadStatus();
          properties = Object.assign({}, properties, { extra_properties: extra });
        }
      } catch (e) {
        /* ad load probe 실패로 전환 트래킹을 막지 않음 */
      }
    }

    var event = {
      event: eventName,
      timestamp: Date.now(),
      visitor_id: this.visitorId,
      session_id: this.sessionId,
      page_instance_id: pageContext.pageInstanceId || undefined,
      page_instance_id_source: pageContext.pageInstanceIdSource || undefined,
      page_alb_trace_id: pageContext.pageAlbTraceId || undefined,
      page_alb_trace_id_source: pageContext.pageAlbTraceIdSource || undefined,
      server_visit_id: pageContext.serverVisitId || undefined,
      self_fingerprint: this.selfFingerprint || undefined,
      fpjs_visitor_id: this.fpjsVisitorId || undefined,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      title: typeof document !== 'undefined' ? document.title : undefined,
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
      properties: Object.assign({
        screen_width: typeof screen !== 'undefined' ? screen.width : undefined,
        screen_height: typeof screen !== 'undefined' ? screen.height : undefined,
        viewport_width: typeof window !== 'undefined' ? window.innerWidth : undefined,
        viewport_height: typeof window !== 'undefined' ? window.innerHeight : undefined,
        language: typeof navigator !== 'undefined' ? navigator.language : undefined,
        timezone: (typeof Intl !== 'undefined') ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined,
        search: typeof window !== 'undefined' ? window.location.search : undefined,
        landing_url_raw: this.landingContext ? this.landingContext.landingUrl : undefined,
        landing_query_raw: landingQuery || undefined,
        page_url: typeof window !== 'undefined' ? window.location.href : undefined,
        referrer_url: typeof document !== 'undefined' ? document.referrer : undefined,
        brand: typeof document !== 'undefined' ? document.body && document.body.dataset ? document.body.dataset.brand : undefined : undefined,
        referrer_brand: typeof document !== 'undefined' ? document.body && document.body.dataset ? document.body.dataset.referrerBrand : undefined : undefined,
        page_instance_id: pageContext.pageInstanceId || undefined,
        page_instance_id_source: pageContext.pageInstanceIdSource || undefined,
        page_alb_trace_id: pageContext.pageAlbTraceId || undefined,
        page_alb_trace_id_source: pageContext.pageAlbTraceIdSource || undefined,
        server_visit_id: pageContext.serverVisitId || undefined,
        event_name: eventName,
        occurred_at: new Date().toISOString(),
        form_type: properties.form_type || properties.formType || undefined,
        conversion_detail: properties.conversion_detail || properties.conversionDetail || undefined,
        product_type: properties.product_type || properties.productType || undefined,
        phone_hash: properties.phone_hash || undefined,
        server_session_id: this.sessionId,
        channel_group: properties.channel_group || undefined,
        ...marketingContext,
      }, properties)
    };

    // 큐에 추가
    this.queue.push(event);

    // 배치 크기 도달 시 즉시 전송
    if (this.queue.length >= this.batchSize) {
      this.flush();
    }

    // 오프라인 큐 재시도 (온라인 복귀 시)
    if (this.queue.length === 1) {
      this._retryOfflineQueue();
    }
  };

  /**
   * 배치 플러시
   */
  Analytics.prototype.flush = function () {
    if (this.queue.length === 0) return;
    
    var eventsToSend = this.queue.slice(0, 100); // 최대 100개
    this.queue = this.queue.slice(100);
    
    this._sendWithRetry(this.endpoint + '/events/batch', eventsToSend, 0)
      .catch(function () {
        // 에러는 이미 처리됨 (오프라인 큐 저장)
      });
  };

  /**
   * 주기적 배치 플러시
   */
  Analytics.prototype._startBatchFlusher = function () {
    var self = this;
    this.flushTimer = setInterval(function () {
      self.flush();
    }, this.flushInterval);
  };

  /**
   * 페이지 언로드 시 처리
   */
  Analytics.prototype._handleBeforeUnload = function () {
    this.flush();
  };

  /**
   * 페이지 뷰 트래킹
   */
  Analytics.prototype.pageView = function (properties) {
    this.track('page_view', properties);
    // S2: 이 page_view가 실제로 쓴 페이지 컨텍스트를 고정 — 이후 폼제출 스냅샷이 동일 pi_를 재사용.
    // track()이 직전에 refreshPageContext로 this.pageContext를 최신 meta로 맞춘 상태다.
    this.activePageContext = this.pageContext;
  };

  /**
   * S3: SPA 라우트 변경 시 page_view 재발화. App Router 등은 history API로 네비게이션하므로
   * pushState/replaceState 패치 + popstate로 감지한다(싱글턴 1회 설정). 이렇게 각 페이지가
   * 자기 page_view를 남기고, activePageContext(S2)도 그 페이지 값으로 갱신된다.
   */
  Analytics.prototype._startSpaPageViewTracking = function () {
    if (typeof window === 'undefined' || this._spaTracking) return;
    this._spaTracking = true;
    var self = this;
    var fire = function () {
      try { self.pageView(); } catch (e) { /* ignore */ }
    };
    var origPush = window.history.pushState;
    var origReplace = window.history.replaceState;
    window.history.pushState = function () {
      var ret = origPush.apply(this, arguments);
      fire();
      return ret;
    };
    window.history.replaceState = function () {
      var ret = origReplace.apply(this, arguments);
      fire();
      return ret;
    };
    window.addEventListener('popstate', fire);
  };

  /**
   * 사용자 식별
   */
  Analytics.prototype.identify = function (userId, traits) {
    this.visitorId = userId;
    this._writeVisitorCookie('analytics_visitor_id', userId);  // 크로스서브도메인 공유
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('analytics_visitor_id', userId);
    }
    this.track('identify', traits || {});
  };

  Analytics.prototype.identifyDistinct = function (fpjsVisitorId, traits) {
    this.fpjsVisitorId = fpjsVisitorId;
    this.track('identify_distinct', Object.assign({}, traits || {}, {
      fpjs_visitor_id: fpjsVisitorId,
    }));
  };

  /**
   * 문의 제출 1회당 서버/폼에 실을 스냅샷 (PII 제외).
   * page_view / batch 이벤트 top-level·properties 키와 align (snake_case).
   * 호출 전 meta 기준으로 page_alb_trace_id 를 맞추기 위해 refreshPageContext 수행.
   * @returns {Record<string, string|undefined|null>}
   */
  Analytics.prototype.getInquiryStitchingSnapshot = function () {
    // S2: 폼 제출 앵커는 '그 페이지 page_view가 고정한 컨텍스트'를 재사용한다 → page_view와 pi_ 일치.
    // page_view 전(고정값 없음)이면 그때만 meta를 재읽어 폴백한다.
    var pageContext = this.activePageContext;
    if (!pageContext) {
      try {
        this.refreshPageContext();
      } catch (e) { /* ignore */ }
      pageContext = this.pageContext || this._readPageInstanceContext();
    }

    var lc = this.landingContext || { landingUrl: null, landingQuery: null };
    return {
      visitor_id: this.visitorId != null ? String(this.visitorId) : null,
      session_id: this.sessionId != null ? String(this.sessionId) : null,
      server_session_id: this.sessionId != null ? String(this.sessionId) : null,
      self_fingerprint: this.selfFingerprint != null ? String(this.selfFingerprint) : null,
      fpjs_visitor_id: this.fpjsVisitorId != null ? String(this.fpjsVisitorId) : null,
      landing_url: lc.landingUrl != null ? String(lc.landingUrl) : null,
      landing_query: lc.landingQuery != null && lc.landingQuery !== ''
        ? String(lc.landingQuery)
        : null,
      page_instance_id: pageContext.pageInstanceId != null ? String(pageContext.pageInstanceId) : null,
      page_instance_id_source: pageContext.pageInstanceIdSource != null ? String(pageContext.pageInstanceIdSource) : null,
      page_alb_trace_id: pageContext.pageAlbTraceId != null ? String(pageContext.pageAlbTraceId) : null,
      page_alb_trace_id_source: pageContext.pageAlbTraceIdSource != null ? String(pageContext.pageAlbTraceIdSource) : null,
      server_visit_id: pageContext.serverVisitId != null ? String(pageContext.serverVisitId) : null,
    };
  };

  /**
   * 타사 광고·마케팅 태그 로드/준비 상태 스냅샷.
   * 홈페이지가 window.__EC_AD_VENDOR_LOAD__ 레지스트리를 채우면 configured/script_status를 반영하고,
   * 없으면 window 글로벌 probe만으로 runtime_ready를 채운다.
   * @returns {{version:1,vendors:Object,summary:{configured:number,loaded:number,ready:number,errored:number}}}
   */
  Analytics.prototype.getAdVendorLoadStatus = function () {
    var VENDOR_IDS = [
      'google_analytics',
      'google_tag_manager',
      'google_ads',
      'meta',
      'naver',
      'kakao',
      'daangn',
      'smartlog',
      'tiktok',
    ];
    var now = new Date().toISOString();
    var w = typeof window !== 'undefined' ? window : null;
    var reg = w && w.__EC_AD_VENDOR_LOAD__ ? w.__EC_AD_VENDOR_LOAD__ : null;

    function probeReady(id) {
      if (!w) return false;
      switch (id) {
        case 'google_analytics':
        case 'google_ads':
          return typeof w.gtag === 'function';
        case 'google_tag_manager':
          return Array.isArray(w.dataLayer) && typeof w.google_tag_manager === 'object';
        case 'meta':
          return typeof w.fbq === 'function' && typeof w.fbq.callMethod === 'function';
        case 'naver':
          return typeof w.wcs === 'object' && w.wcs != null && typeof w.wcs.trans === 'function';
        case 'kakao':
          return typeof w.kakaoPixel === 'function';
        case 'daangn':
          return typeof w.karrotPixel === 'object' && w.karrotPixel != null && typeof w.karrotPixel.track === 'function';
        case 'smartlog':
          return typeof w.hpt_trace_info !== 'undefined' || typeof w.smlog !== 'undefined';
        case 'tiktok':
          return typeof w.ttq === 'object' && w.ttq != null;
        default:
          return false;
      }
    }

    var vendors = {};
    var configured = 0;
    var loaded = 0;
    var ready = 0;
    var errored = 0;

    for (var i = 0; i < VENDOR_IDS.length; i++) {
      var id = VENDOR_IDS[i];
      var isConfigured = !!(reg && reg.configured && reg.configured[id]);
      var scriptStatus =
        reg && reg.script_status && reg.script_status[id]
          ? reg.script_status[id]
          : isConfigured
            ? 'pending'
            : 'unknown';
      var runtimeReady = probeReady(id);
      vendors[id] = {
        configured: isConfigured,
        script_status: scriptStatus,
        runtime_ready: runtimeReady,
        checked_at: now,
      };
      if (isConfigured) configured += 1;
      if (scriptStatus === 'loaded') loaded += 1;
      if (runtimeReady) ready += 1;
      if (scriptStatus === 'error') errored += 1;
    }

    return {
      version: 1,
      vendors: vendors,
      summary: {
        configured: configured,
        loaded: loaded,
        ready: ready,
        errored: errored,
      },
    };
  };

  /**
   * 정리
   */
  Analytics.prototype.destroy = function () {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flush();
  };

  // Export — 두 채널 모두에 등록한다.
  //  * module.exports: esbuild IIFE 번들 내부의 require('./analytics.js') 가 받음
  //  * global.Analytics: 같은 IIFE 안에서 import 없이 직접 window.Analytics를 보는 부트스트랩
  //    (sdk-entry.verify.js 가 이 경로로 받음)
  // 과거에는 if-else 였는데, esbuild IIFE 내부에서는 module 객체가 항상 truthy 이므로
  // global.Analytics 가 영영 설정되지 않아 sdk-entry.verify.js 가
  // "[AnalyticsCollectorVerify] Analytics class is not loaded" 로 throw 했다.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = Analytics;
  }
  if (global) {
    global.Analytics = Analytics;
  }
})(typeof window !== 'undefined' ? window : (typeof global !== 'undefined' ? global : null));

// ===== 사용 예제 =====
// 
// // 초기화
// var analytics = new Analytics({
//   endpoint: 'https://your-alb-dns.com',
//   apiKey: 'your-api-key',  // 프로덕션에서만
//   batchSize: 10,
//   flushInterval: 5000
// });
// 
// // 페이지 뷰
// analytics.pageView();
// 
// // 커스텀 이벤트
// analytics.track('button_click', {
//   buttonId: 'signup',
//   location: 'hero'
// });
// 
// // 사용자 식별
// analytics.identify('user_12345', {
//   email: 'user@example.com'
// });

